/**
 * Central Tracking Façade — iter158 (2026-07-07)
 *
 * Single point of entry for ALL analytics/marketing tracking events across
 * the app. Each function does three things (unless noted):
 *
 *   1. Pushes to `window.dataLayer` so GTM tags and downstream ad platforms
 *      (GA4, Meta, TikTok, LinkedIn, Microsoft Clarity, Hotjar) receive the
 *      event without waiting for a backend round-trip.
 *   2. POSTs the event to our own `/api/analytics/track` so the internal
 *      Marketing Dashboard has the same data.
 *   3. Carries the first-touch attribution bundle (utm_*, gclid, fbclid,
 *      referrer, landing_url) already captured in localStorage.
 *
 * NEW integrations (Clarity, Hotjar, LinkedIn Ads, etc.) plug in HERE — no
 * scattered edits across the app. Just add a `window.<vendor>?.track(...)`
 * call inside the relevant function once you have the vendor's snippet
 * pasted into `index.html` via GTM.
 *
 * ⚠️ SERVER-CONFIRMED CONVERSIONS ONLY:
 *   • `sign_up`   — MUST be called from the `.then()` of a successful
 *                    `/api/auth/register` response (never from page view).
 *   • `login`     — same for `/api/auth/login`.
 *   • `purchase`  — MUST be called only after Stripe webhook confirmation.
 */

import Analytics from './analytics';

const DL_KEY = 'dataLayer';

function pushDataLayer(payload) {
  if (typeof window === 'undefined') return;
  window[DL_KEY] = window[DL_KEY] || [];
  try {
    window[DL_KEY].push(payload);
  } catch (_) { /* no-op: analytics must never break the app */ }
}

// ── First-visit tracking (new vs returning) ──────────────────────────
const FIRST_VISIT_KEY = 'pp_first_visit_at';

function ensureFirstVisitStamp() {
  if (typeof window === 'undefined') return null;
  let stamp = window.localStorage.getItem(FIRST_VISIT_KEY);
  if (!stamp) {
    stamp = new Date().toISOString();
    try { window.localStorage.setItem(FIRST_VISIT_KEY, stamp); } catch (_) { /* SSR / quota */ }
    return { first_visit_at: stamp, is_new_visitor: true };
  }
  return { first_visit_at: stamp, is_new_visitor: false };
}

// ── Cheap client-side device/browser/OS detection ────────────────────
function getDeviceMeta() {
  if (typeof window === 'undefined') return {};
  const ua = navigator.userAgent || '';
  const width = window.innerWidth || 0;

  // Device
  let deviceType = 'desktop';
  if (/Mobi|Android|iPhone|iPod/i.test(ua) && width < 768) deviceType = 'mobile';
  else if (/iPad|Tablet/i.test(ua) || (width >= 768 && width < 1024)) deviceType = 'tablet';

  // Browser
  let browser = 'unknown';
  if (/Edg\//.test(ua)) browser = 'edge';
  else if (/OPR\//.test(ua) || /Opera/.test(ua)) browser = 'opera';
  else if (/Chrome\//.test(ua)) browser = 'chrome';
  else if (/Safari\//.test(ua)) browser = 'safari';
  else if (/Firefox\//.test(ua)) browser = 'firefox';

  // OS
  let os = 'unknown';
  if (/Windows/.test(ua)) os = 'windows';
  else if (/Mac OS X/.test(ua)) os = 'mac';
  else if (/Android/.test(ua)) os = 'android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'ios';
  else if (/Linux/.test(ua)) os = 'linux';

  return { device_type: deviceType, browser, os };
}

// ── Public API ───────────────────────────────────────────────────────

// Session-scoped guard so we NEVER double-fire a top-of-funnel event even
// when React StrictMode double-mounts components in development, or when
// a user hits back/forward and the effect re-runs. Google Ads / GA4 must
// receive one signal per real page load.
const _firedThisSession = new Set();
function fireOnce(key, fn) {
  if (_firedThisSession.has(key)) return;
  _firedThisSession.add(key);
  fn();
}

/** Called ONCE on landing page mount. Also captures first-touch UTM. */
export function trackLandingView(pageName = 'homepage', extra = {}) {
  fireOnce(`landing_view:${pageName}`, () => {
    Analytics.captureFirstTouchAttribution();
    const visitorMeta = ensureFirstVisitStamp();
    const deviceMeta = getDeviceMeta();
    const meta = { ...visitorMeta, ...deviceMeta, page: pageName, ...extra };
    pushDataLayer({ event: 'landing_page_view', ...meta });
    Analytics.track('landing_page_view', null, meta);
  });
}

/** Every "Get Started" / hero / footer CTA click. */
export function trackGetStartedClick(position, extra = {}) {
  const meta = { position, ...getDeviceMeta(), ...extra };
  pushDataLayer({ event: 'get_started_click', ...meta });
  Analytics.track('get_started_click', null, meta);
}

/** Called on `/register` page mount. */
export function trackRegisterPageView() {
  fireOnce('register_page_view', () => {
    const meta = { ...ensureFirstVisitStamp(), ...getDeviceMeta() };
    pushDataLayer({ event: 'register_page_view', ...meta });
    Analytics.track('register_page_view', null, meta);
  });
}

/** Called the FIRST time the user focuses / types in any register form field. */
export function trackRegisterStarted() {
  const meta = getDeviceMeta();
  pushDataLayer({ event: 'register_started', ...meta });
  Analytics.track('register_started', null, meta);
}

/**
 * Called ONLY from the .then() of a successful POST /api/auth/register.
 * This is the server-confirmed sign-up event that Google Ads should
 * optimise on.
 */
export function trackRegisterCompleted(userId, method = 'email', extra = {}) {
  const meta = { user_id: userId, method, ...getDeviceMeta(), ...extra };
  pushDataLayer({ event: 'sign_up', ...meta });   // GA4 recommended event
  Analytics.track('register_completed', userId, meta);
}

/** Called ONLY from the .then() of a successful POST /api/auth/login. */
export function trackLoginCompleted(userId, extra = {}) {
  const meta = { user_id: userId, method: 'email', ...getDeviceMeta(), ...extra };
  pushDataLayer({ event: 'login', ...meta });
  Analytics.track('login', userId, meta);
}

/** Called when the user CLICKS a subscribe button (before Stripe checkout). */
export function trackBeginSubscription(userId, tier, price, currency = 'GBP') {
  const meta = { user_id: userId, tier, value: price, currency, ...getDeviceMeta() };
  pushDataLayer({ event: 'begin_subscription', ...meta, ecommerce: { currency, value: price, items: [{ item_id: tier, item_name: tier, price }] } });
  Analytics.track('begin_subscription', userId, meta);
}

/**
 * Called ONLY after a webhook-confirmed successful Stripe payment
 * (or a frontend `/payments/me` check that shows subscription_status: active).
 * This is the true purchase event Google Ads should optimise on for ROAS.
 */
export function trackPurchaseCompleted(userId, tier, price, currency = 'GBP', transactionId = null) {
  const meta = {
    user_id: userId, tier, value: price, currency, transaction_id: transactionId,
    ...getDeviceMeta(),
  };
  pushDataLayer({
    event: 'purchase',
    transaction_id: transactionId,
    ecommerce: {
      currency,
      value: price,
      transaction_id: transactionId,
      items: [{ item_id: tier, item_name: tier, price }],
    },
    user_id: userId,
  });
  Analytics.track('purchase_completed', userId, meta);
}

// ── Phase 1 (iter 159): Behavior Tracking Layer ──────────────────────
//
// These 4 events feed the self-learning pipeline. On the backend they
// map to purchase-signal rows (services/purchase_signal_service.py)
// which feed user_product_patterns → user_predictions → recommendations.

/** Called when a user opens a product detail card / page. */
export function trackProductView(userId, product = {}) {
  const meta = {
    user_id: userId,
    barcode: product.barcode || null,
    product_name: product.name || product.product_name || null,
    retailer: product.retailer || product.cheapest_retailer || null,
    price: product.price != null ? Number(product.price) : null,
    category: product.category || null,
    ...getDeviceMeta(),
  };
  pushDataLayer({ event: 'view_item', ...meta });
  Analytics.track('product_view', userId, meta);
}

/** Called when a user clicks a "Compare at retailer" / affiliate link. */
export function trackProductClick(userId, product = {}, retailer = null, from = 'product_card') {
  const meta = {
    user_id: userId,
    barcode: product.barcode || null,
    product_name: product.name || product.product_name || null,
    retailer: retailer || product.retailer || null,
    price: product.price != null ? Number(product.price) : null,
    from,
    ...getDeviceMeta(),
  };
  pushDataLayer({ event: 'select_item', ...meta });
  Analytics.track('product_click', userId, meta);
}

/** Called when a user completes a search / query submission. */
export function trackSearch(userId, query, resultCount = null, extra = {}) {
  const meta = {
    user_id: userId,
    query: query ? String(query).slice(0, 200) : '',
    result_count: resultCount,
    ...getDeviceMeta(),
    ...extra,
  };
  pushDataLayer({ event: 'search', ...meta });
  Analytics.track('search', userId, meta);
}

/** Called immediately after a receipt image finishes uploading (before OCR completes). */
export function trackReceiptUpload(userId, receiptId = null, source = 'camera', extra = {}) {
  const meta = {
    user_id: userId,
    receipt_id: receiptId,
    source,   // 'camera' | 'gallery' | 'file_picker' | 'email_import'
    ...getDeviceMeta(),
    ...extra,
  };
  pushDataLayer({ event: 'receipt_upload', ...meta });
  Analytics.track('receipt_upload', userId, meta);
}

const tracking = {
  trackLandingView,
  trackGetStartedClick,
  trackRegisterPageView,
  trackRegisterStarted,
  trackRegisterCompleted,
  trackLoginCompleted,
  trackBeginSubscription,
  trackPurchaseCompleted,
  // Phase 1 — Behavior Tracking Layer
  trackProductView,
  trackProductClick,
  trackSearch,
  trackReceiptUpload,
  getDeviceMeta,
};

export default tracking;
